/**
 * AlloggiatiCheckinTab — Tab "Check-in ospiti" (Fase A)
 *
 * - Paywall: se l'utente non ha l'add-on (€4.99 una tantum lifetime),
 *   mostra una hero "Sblocca check-in digitale" con bottone Acquista
 *   che apre <PaymentForm> embedded Stripe Elements.
 * - Se attivo: form crea prenotazione + lista prenotazioni esistenti
 *   con bottone "Copia link ospite" (per ora il link punta a una
 *   pagina /checkin/:token che verrà costruita in Fase B).
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'sonner';
import { Lock, ShieldCheck, CheckCircle, Plus, House, Users, Copy, Trash, Bed, CalendarBlank, IdentificationCard, PaperPlaneTilt, Warning, Sparkle } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { dateIT } from '../lib/dateIT';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND_URL = window.location.origin;

const stripeAppearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#18181b',
    colorBackground: '#ffffff',
    colorText: '#18181b',
    fontFamily: 'Inter, system-ui, sans-serif',
    borderRadius: '0px',
  },
};

// ---------------- Stripe Payment Form (€4.99) ----------------
function AddonPaymentForm({ paymentIntentId, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/schedine?addon=ok` },
      redirect: 'if_required',
    });
    if (error) {
      toast.error(error.message || 'Errore pagamento');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      try {
        await axios.post(`${API}/alloggiati-addon/verify/${paymentIntent.id}`, {}, { withCredentials: true });
        toast.success('🎉 Modulo Check-in sbloccato!');
        onSuccess();
      } catch (err) {
        toast.error('Pagamento ok ma sblocco fallito. Ricarica la pagina.');
      }
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="addon-stripe-form">
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full bg-violet-700 hover:bg-violet-800 text-white font-black uppercase tracking-wider text-sm py-3.5 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
        data-testid="addon-pay-btn"
      >
        {submitting ? 'Elaborazione...' : (<><Lock size={14} weight="bold" /> Sblocca per € 4,99</>)}
      </button>
      <p className="text-[10px] text-zinc-400 text-center uppercase tracking-wider">
        Pagamento una tantum · Lifetime · Tutti gli alloggi inclusi
      </p>
    </form>
  );
}

// ---------------- Paywall ----------------
function AddonPaywall({ onUnlocked }) {
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  const startPurchase = async () => {
    setLoading(true);
    try {
      const [pkRes, intentRes] = await Promise.all([
        axios.get(`${API}/subscription/public-key`, { withCredentials: true }),
        axios.post(`${API}/alloggiati-addon/intent`, {}, { withCredentials: true }),
      ]);
      if (intentRes.data.already_active) {
        toast.success('Modulo già attivo!');
        onUnlocked();
        return;
      }
      setStripePromise(loadStripe(pkRes.data.publishable_key));
      setClientSecret(intentRes.data.client_secret);
      setPaymentIntentId(intentRes.data.payment_intent_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore inizializzazione pagamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-200 p-6 sm:p-10" data-testid="addon-paywall">
      <div className="max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-900 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider mb-3">
          <IdentificationCard size={11} weight="bold" /> Nuovo · Modulo Check-in
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-zinc-900 tracking-tight mb-2">
          Check-in digitale ospiti.<br />
          <span className="text-violet-700">Documenti scansionati. Schedine inviate. Zero stress.</span>
        </h2>
        <p className="text-sm text-zinc-600 mb-6 leading-relaxed">
          Crei una prenotazione, generi un link multilingua (IT/EN/FR/ES/DE), l'ospite compila i suoi dati con
          la fotocamera del telefono (scansiona il barcode del documento) e tu invii le schedine alla Polizia
          con un click.
          <br />
          <strong className="text-zinc-900">Tutti i dati personali vengono cancellati subito dopo l'invio</strong> (GDPR Art. 5).
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {[
            { ic: Users, t: '5 lingue', d: 'IT · EN · FR · ES · DE auto' },
            { ic: ShieldCheck, t: 'OCR client-side', d: 'Foto mai uploadate' },
            { ic: CheckCircle, t: 'GDPR purge', d: 'Dati cancellati post-invio' },
          ].map((f, i) => (
            <div key={i} className="bg-white border border-zinc-200 p-3">
              <f.ic size={18} weight="bold" className="text-violet-700 mb-1.5" />
              <p className="text-xs font-black text-zinc-900">{f.t}</p>
              <p className="text-[11px] text-zinc-500">{f.d}</p>
            </div>
          ))}
        </div>

        <div className="bg-zinc-900 text-white p-5 sm:p-6 mb-4">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-4xl font-black tracking-tight">€ 4,99</span>
            <span className="text-xs text-zinc-400 uppercase tracking-wider">una tantum</span>
          </div>
          <p className="text-xs text-zinc-300 mb-4">
            Lifetime · Tutti i tuoi alloggi · Aggiornamenti inclusi · Niente abbonamento aggiuntivo
          </p>

          {!clientSecret ? (
            <button
              onClick={startPurchase}
              disabled={loading}
              className="w-full bg-white text-zinc-900 hover:bg-violet-50 font-black uppercase tracking-wider text-sm py-3.5 transition-colors flex items-center justify-center gap-2"
              data-testid="addon-buy-btn"
            >
              {loading ? 'Caricamento...' : (<><Lock size={14} weight="bold" /> Sblocca ora</>)}
            </button>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance, locale: 'it' }}>
              <AddonPaymentForm paymentIntentId={paymentIntentId} onSuccess={onUnlocked} />
            </Elements>
          )}
        </div>

        <p className="text-[11px] text-zinc-500 text-center">
          Pagamento sicuro Stripe · Carta non condivisa con noi · Disponibile subito dopo conferma
        </p>
      </div>
    </div>
  );
}

// ---------------- Booking form + list ----------------
function BookingForm({ strutture, fallbackProperties, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  // Strutture (from Alloggiati Web) take priority. Each item has {id, name, address}.
  // We use string ids prefixed with "alloggiati:" or "property:" to disambiguate.
  const allOptions = [
    ...strutture.map(s => ({ value: `alloggiati:${s.id}`, label: s.name + (s.address ? ` · ${s.address}` : ''), source: 'alloggiati', raw: s })),
    ...fallbackProperties.map(p => ({ value: `property:${p.id}`, label: p.name + (p.property_city ? ` · ${p.property_city}` : ''), source: 'property', raw: p })),
  ];

  const [form, setForm] = useState({
    selection: allOptions[0]?.value || '',
    check_in: today,
    nights: 1,
    guests_count: 1,
    check_in_by: 'guest',
    primary_name: '',
    primary_email: '',
    primary_phone: '',
    price: 0,
    deposit: 0,
    registration_type: 'singolo',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  // Update default selection if strutture loaded after first render
  useEffect(() => {
    if (!form.selection && allOptions.length > 0) {
      setForm(prev => ({ ...prev, selection: allOptions[0].value }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strutture.length, fallbackProperties.length]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.selection) { toast.error('Seleziona una struttura'); return; }
    setSaving(true);
    try {
      const opt = allOptions.find(o => o.value === form.selection);
      const payload = {
        check_in: form.check_in,
        nights: form.nights,
        guests_count: form.guests_count,
        check_in_by: form.check_in_by,
        primary_name: form.primary_name,
        primary_email: form.primary_email,
        primary_phone: form.primary_phone,
        price: form.price,
        deposit: form.deposit,
        registration_type: form.registration_type,
        notes: form.notes,
      };
      if (opt?.source === 'alloggiati') {
        payload.alloggiati_struttura_id = opt.raw.id;
        payload.alloggiati_struttura_name = opt.raw.name;
      } else if (opt?.source === 'property') {
        payload.property_id = opt.raw.id;
      }
      const res = await axios.post(`${API}/alloggiati-addon/bookings`, payload, { withCredentials: true });
      toast.success('Prenotazione creata');
      onCreated(res.data);
      setForm(prev => ({ ...prev, primary_name: '', primary_email: '', primary_phone: '', notes: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-zinc-200 p-5 sm:p-6 space-y-4" data-testid="booking-create-form">
      <div className="flex items-center gap-2">
        <Plus size={16} weight="bold" className="text-violet-700" />
        <h3 className="font-black text-sm uppercase tracking-wider text-zinc-900">Nuova prenotazione</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Struttura (da Alloggiati Web)">
          <select value={form.selection} onChange={(e) => update('selection', e.target.value)} className="w-full h-10 px-3 border border-zinc-300 text-sm" data-testid="booking-property" required>
            {allOptions.length === 0 && <option value="">— Nessuna struttura disponibile —</option>}
            {allOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Chi farà il check-in?">
          <select value={form.check_in_by} onChange={(e) => update('check_in_by', e.target.value)} className="w-full h-10 px-3 border border-zinc-300 text-sm" data-testid="booking-checkin-by">
            <option value="guest">Ospite (con link)</option>
            <option value="host">Io (in loco)</option>
          </select>
        </Field>
        <Field label="Data check-in">
          <input type="date" value={form.check_in} onChange={(e) => update('check_in', e.target.value)} className="w-full h-10 px-3 border border-zinc-300 text-sm" data-testid="booking-check-in" required />
        </Field>
        <Field label="Numero notti">
          <input type="number" min="1" max="30" value={form.nights} onChange={(e) => update('nights', Math.min(30, Math.max(1, Number(e.target.value) || 1)))} className="w-full h-10 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="booking-nights" required />
        </Field>
        <Field label="Numero ospiti">
          <input type="number" min="1" max="30" value={form.guests_count} onChange={(e) => update('guests_count', Math.min(30, Math.max(1, Number(e.target.value) || 1)))} className="w-full h-10 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="booking-guests" required />
        </Field>
        <Field label="Tipo registrazione">
          <select value={form.registration_type} onChange={(e) => update('registration_type', e.target.value)} className="w-full h-10 px-3 border border-zinc-300 text-sm" data-testid="booking-regtype">
            <option value="singolo">Ospite singolo</option>
            <option value="famiglia">Famiglia</option>
            <option value="gruppo">Gruppo</option>
          </select>
        </Field>
        <Field label="Email ospite (facoltativo)">
          <input type="email" value={form.primary_email} onChange={(e) => update('primary_email', e.target.value)} className="w-full h-10 px-3 border border-zinc-300 text-sm" placeholder="ospite@email.com" data-testid="booking-email" />
        </Field>
        <Field label="Nome ospite (facoltativo)">
          <input type="text" value={form.primary_name} onChange={(e) => update('primary_name', e.target.value)} className="w-full h-10 px-3 border border-zinc-300 text-sm" placeholder="Mario Rossi" data-testid="booking-name" />
        </Field>
        <Field label="Telefono (facoltativo)">
          <input type="tel" value={form.primary_phone} onChange={(e) => update('primary_phone', e.target.value)} className="w-full h-10 px-3 border border-zinc-300 text-sm" placeholder="+39..." data-testid="booking-phone" />
        </Field>
        <Field label="Prezzo (facoltativo, €)">
          <input type="number" step="0.01" min="0" value={form.price} onChange={(e) => update('price', Number(e.target.value) || 0)} className="w-full h-10 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="booking-price" />
        </Field>
        <Field label="Deposito (facoltativo, €)">
          <input type="number" step="0.01" min="0" value={form.deposit} onChange={(e) => update('deposit', Number(e.target.value) || 0)} className="w-full h-10 px-3 border border-zinc-300 text-sm tabular-nums" data-testid="booking-deposit" />
        </Field>
      </div>

      <Field label="Note interne (facoltative)">
        <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)} rows="2" className="w-full px-3 py-2 border border-zinc-300 text-sm" placeholder="es. arrivo dopo le 22:00" data-testid="booking-notes" />
      </Field>

      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <Button type="submit" disabled={saving || !form.selection} className="rounded-none bg-violet-700 hover:bg-violet-800 text-white text-xs font-black uppercase tracking-wider h-11 flex-1" data-testid="booking-submit">
          {saving ? 'Salvataggio...' : (<><Plus size={13} weight="bold" className="mr-1" /> Crea prenotazione + Link</>)}
        </Button>
      </div>
    </form>
  );
}

function BookingList({ items, onDelete, onSend, sendingId }) {
  if (!items.length) {
    return (
      <div className="bg-zinc-50 border border-dashed border-zinc-300 p-8 text-center" data-testid="bookings-empty">
        <Bed size={32} weight="duotone" className="mx-auto text-zinc-400 mb-2" />
        <p className="text-sm font-bold text-zinc-700">Nessuna prenotazione</p>
        <p className="text-xs text-zinc-500 mt-1">Crea la prima qui sopra o importa via iCal</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="bookings-list">
      {items.map(b => <BookingCard key={b.id} b={b} onDelete={onDelete} onSend={onSend} sending={sendingId === b.id} />)}
    </div>
  );
}

function BookingCard({ b, onDelete, onSend, sending }) {
  const link = `${FRONTEND_URL}/checkin/${b.link_token}`;
  const copy = () => {
    navigator.clipboard.writeText(link);
    toast.success('Link copiato negli appunti');
  };

  const statusColor = {
    draft: 'bg-zinc-100 text-zinc-700 border-zinc-300',
    pending_guest: 'bg-amber-50 text-amber-800 border-amber-200',
    completed: 'bg-blue-50 text-blue-800 border-blue-200',
    sent: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    purged: 'bg-zinc-50 text-zinc-500 border-zinc-200',
  }[b.status] || 'bg-zinc-100';

  const statusLabel = {
    draft: 'Bozza',
    pending_guest: 'In attesa ospite',
    completed: 'Pronto invio',
    sent: 'Trasmesso',
    purged: 'Cancellato (GDPR)',
  }[b.status] || b.status;

  const canSend = b.status === 'completed';

  return (
    <div className="bg-white border border-zinc-200 p-4 sm:p-5" data-testid={`booking-card-${b.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 border ${statusColor}`}>{statusLabel}</span>
            {b.source === 'ical' && <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200">iCal</span>}
            <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">
              {b.guests_completed || 0}/{b.guests_count} ospit{b.guests_count === 1 ? 'e' : 'i'}
            </span>
            {b.transmitted_at && (
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700">
                inviato il {b.transmitted_at.slice(0, 10)}
              </span>
            )}
          </div>
          <p className="text-sm font-black text-zinc-900 truncate">{b.primary_name || 'Ospite'}</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            <House size={11} weight="bold" className="inline mr-1" />{b.property_name || '—'}
            {b.alloggiati_struttura_id && (
              <span className="ml-1 text-[10px] font-bold text-violet-700 uppercase tracking-wider">
                · ID Polizia {b.alloggiati_struttura_id}
              </span>
            )}
          </p>
          <p className="text-xs text-zinc-500 mt-0.5">
            <CalendarBlank size={11} weight="bold" className="inline mr-1" />
            Dal {dateIT(b.check_in)} al {dateIT(b.check_out)} · {b.nights} nott{b.nights === 1 ? 'e' : 'i'}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {b.status !== 'sent' && b.status !== 'purged' && !canSend && (
            <button onClick={copy} className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 bg-violet-700 text-white hover:bg-violet-800 transition-colors flex items-center gap-1" data-testid={`booking-copy-${b.id}`}>
              <Copy size={11} weight="bold" /> Copia link
            </button>
          )}
          {b.status !== 'sent' && (
            <button onClick={() => onDelete(b.id)} className="text-zinc-400 hover:text-red-600 p-1.5" data-testid={`booking-delete-${b.id}`}>
              <Trash size={13} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Send to Polizia (Phase D) — visible only when all guests submitted */}
      {canSend && (
        <div className="mt-3 pt-3 border-t border-blue-100 bg-blue-50/40 -m-4 sm:-m-5 mt-3 p-4 sm:p-5">
          <div className="flex items-start gap-3 mb-3">
            <ShieldCheck size={18} weight="fill" className="text-blue-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-blue-900">Pronto per la Polizia di Stato</p>
              <p className="text-[11px] text-blue-800 mt-0.5 leading-snug">
                Tutti i {b.guests_count} ospit{b.guests_count === 1 ? 'e ha' : 'i hanno'} completato la registrazione. Puoi inviare ora oppure aspettare l'<strong>auto-invio automatico alle 23:30</strong>.
              </p>
            </div>
          </div>
          <button
            onClick={() => onSend(b.id)}
            disabled={sending}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white font-black uppercase tracking-wider text-sm py-3 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            data-testid={`booking-send-${b.id}`}
          >
            {sending ? 'Trasmissione in corso…' : (
              <>
                <PaperPlaneTilt size={14} weight="bold" /> Invia alla Polizia ora
              </>
            )}
          </button>
          <p className="text-[10px] text-blue-700/80 text-center mt-1.5">
            🗑️ Dopo l'invio i dati personali vengono cancellati immediatamente (GDPR)
          </p>
        </div>
      )}

      {/* Last error message (failed manual or auto attempt) */}
      {b.last_send_error && b.status !== 'sent' && (
        <div className="mt-3 pt-3 border-t border-red-100 flex items-start gap-2">
          <Warning size={13} weight="bold" className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-red-700">Ultimo errore</p>
            <p className="text-[11px] text-red-700 leading-snug">{b.last_send_error}</p>
          </div>
        </div>
      )}

      {b.status !== 'sent' && b.status !== 'purged' && !canSend && (
        <div className="mt-3 pt-3 border-t border-zinc-100">
          <p className="text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-1">Link ospite (multilingua)</p>
          <p className="text-[11px] text-zinc-600 font-mono truncate">{link}</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

// ---------------- Coming Soon banner (pre-launch hide for non-admin) ----------------
function ComingSoonBanner() {
  return (
    <div className="bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-500 text-white p-6 sm:p-12 relative overflow-hidden" data-testid="checkin-coming-soon">
      <div className="absolute -top-12 -right-12 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-16 -left-16 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
      <div className="relative max-w-2xl">
        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 mb-4">
          <Sparkle size={12} weight="fill" className="text-yellow-200" />
          <span className="text-[10px] font-black uppercase tracking-[0.2em]">Prossimamente</span>
        </div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-3">
          Check-in digitale ospiti<br />
          <span className="text-white/85">è in arrivo.</span>
        </h2>
        <p className="text-sm sm:text-base text-white/90 leading-relaxed mb-6 max-w-xl">
          Stiamo finendo di rifinire il modulo Premium che ti permetterà di inviare un link multilingua all'ospite,
          fargli scansionare il documento con la fotocamera (zero upload immagini · GDPR by design)
          e trasmettere automaticamente le schedine alla Polizia di Stato alle 23:30 ogni sera.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          {[
            { ic: Users, t: '5 lingue auto', d: 'IT · EN · FR · ES · DE' },
            { ic: ShieldCheck, t: 'OCR client-side', d: 'Foto mai uploadate' },
            { ic: CheckCircle, t: 'GDPR purge', d: 'Auto-cancellazione post-invio' },
          ].map((f, i) => (
            <div key={i} className="bg-white/10 backdrop-blur-sm p-3">
              <f.ic size={18} weight="bold" className="text-white mb-1.5" />
              <p className="text-xs font-black">{f.t}</p>
              <p className="text-[10px] text-white/80">{f.d}</p>
            </div>
          ))}
        </div>

        <div className="bg-zinc-900/40 backdrop-blur-sm border border-white/15 p-4">
          <p className="text-xs text-white/90 leading-relaxed">
            <strong className="text-white">Sarà un add-on a parte di € 4,99 una tantum (lifetime).</strong>
            <br />
            Pagamento singolo, infiniti alloggi, infiniti check-in. Nessun costo ricorrente.
          </p>
        </div>

        <p className="text-[11px] text-white/70 mt-6 italic">
          Ti avviseremo via email quando sarà disponibile · grazie per la pazienza! 🙏
        </p>
      </div>
    </div>
  );
}


// ---------------- Main tab component ----------------
export default function AlloggiatiCheckinTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [addon, setAddon] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [strutture, setStrutture] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [strutturePending, setStrutturePending] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    try {
      const [aRes, pRes] = await Promise.all([
        axios.get(`${API}/alloggiati-addon/status`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true }),
      ]);
      setAddon(aRes.data);
      setProperties(pRes.data || []);
      if (aRes.data.active) {
        const bRes = await axios.get(`${API}/alloggiati-addon/bookings`, { withCredentials: true });
        setBookings(bRes.data.items || []);
        // Fetch strutture from Alloggiati Web (may fail if config missing — non-blocking)
        setStrutturePending(true);
        try {
          const sRes = await axios.get(`${API}/alloggiati/strutture`, { withCredentials: true });
          setStrutture(sRes.data?.items || []);
        } catch (_) {
          setStrutture([]);
        } finally {
          setStrutturePending(false);
        }
      }
    } catch (err) {
      console.error('addon fetch', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  const refreshBookings = useCallback(async () => {
    try {
      const bRes = await axios.get(`${API}/alloggiati-addon/bookings`, { withCredentials: true });
      setBookings(bRes.data.items || []);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchAll();
    // Auto-verify if redirected back from Stripe (?addon=ok)
    if (window.location.search.includes('addon=ok')) {
      window.history.replaceState({}, '', window.location.pathname);
      toast.success('Pagamento completato! Modulo sbloccato.');
      setTimeout(fetchAll, 1500);
    }
  }, [fetchAll]);

  // Light polling so the host sees status transitions when guests complete the form
  useEffect(() => {
    if (!addon?.active) return;
    const id = setInterval(refreshBookings, 30000);
    return () => clearInterval(id);
  }, [addon?.active, refreshBookings]);

  const onCreated = (booking) => {
    setBookings(prev => [booking, ...prev]);
    // Auto-copy link if guest-checkin
    if (booking.check_in_by === 'guest') {
      const link = `${FRONTEND_URL}/checkin/${booking.link_token}`;
      navigator.clipboard.writeText(link).catch(() => {});
      toast.success('Link copiato — invialo all\'ospite via WhatsApp/email', { duration: 5000 });
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Eliminare questa prenotazione?')) return;
    try {
      await axios.delete(`${API}/alloggiati-addon/bookings/${id}`, { withCredentials: true });
      setBookings(prev => prev.filter(b => b.id !== id));
      toast.success('Prenotazione eliminata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const onSyncLookup = async () => {
    if (!window.confirm('Scaricare la lista ufficiale Comuni + Stati dal portale Polizia?\n\nRichiede credenziali Alloggiati Web configurate. Operazione lenta (10-30 sec).')) return;
    try {
      const res = await axios.post(`${API}/alloggiati-addon/lookup/sync`, {}, { withCredentials: true });
      const summary = res.data?.summary || {};
      const comuniCount = summary.alloggiati_lookup_comuni?.count || 0;
      const statiCount = summary.alloggiati_lookup_stati?.count || 0;
      toast.success(`✅ Tabelle aggiornate: ${comuniCount} comuni, ${statiCount} stati`, { duration: 6000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore sincronizzazione');
    }
  };

  const onSend = async (id) => {
    if (!window.confirm('Confermi l\'invio delle schedine alla Polizia di Stato?\n\nDopo la trasmissione i dati personali degli ospiti saranno cancellati immediatamente (GDPR).')) return;
    setSendingId(id);
    try {
      const res = await axios.post(`${API}/alloggiati-addon/bookings/${id}/send`, {}, { withCredentials: true });
      toast.success(`✅ ${res.data.records_count} schedin${res.data.records_count === 1 ? 'a inviata' : 'e inviate'} alla Polizia. Dati cancellati.`, { duration: 6000 });
      await refreshBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore trasmissione', { duration: 8000 });
      await refreshBookings();
    } finally {
      setSendingId(null);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-zinc-400 text-sm">Caricamento...</div>;
  }

  // Pre-launch gate: hide the entire module from non-admin users with a "Coming soon" banner.
  // Admin (sergio.battelli@gmail.com, admin@locazioneturistica.com) bypasses for testing.
  if (!isAdmin) {
    return <ComingSoonBanner />;
  }

  if (!addon?.active) {
    return <AddonPaywall onUnlocked={fetchAll} />;
  }

  return (
    <div className="space-y-5" data-testid="checkin-tab-content">
      {/* Status banner */}
      <div className="bg-gradient-to-br from-violet-700 to-fuchsia-700 text-white p-4 sm:p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/80 mb-0.5">Modulo attivo</p>
          <p className="text-base font-black tracking-tight">Check-in digitale ospiti</p>
          <p className="text-[11px] text-white/75 mt-0.5">
            {addon.is_admin_bypass ? 'Admin bypass · accesso illimitato' : `Acquistato · ${addon.purchased_at?.slice(0, 10) || 'lifetime'}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSyncLookup}
            className="text-[10px] font-black uppercase tracking-wider bg-white/15 hover:bg-white/25 text-white px-3 py-2 transition-colors flex items-center gap-1.5"
            data-testid="sync-lookup-btn"
            title="Aggiorna le tabelle Comuni + Stati dal portale Polizia"
          >
            ⬇ Sincronizza tabelle
          </button>
          <CheckCircle size={32} weight="fill" className="text-white/80" />
        </div>
      </div>

      {/* Strutture guard */}
      {strutture.length === 0 && properties.length === 0 && !strutturePending && (
        <div className="bg-amber-50 border border-amber-200 p-4 text-xs text-amber-900">
          <strong>Configura Alloggiati Web</strong> in <em>Configurazione</em> per caricare automaticamente le tue strutture (utente WSKEY + password). Le strutture vengono lette direttamente dal portale Polizia di Stato.
        </div>
      )}

      {strutture.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 p-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-blue-900">
            <strong>{strutture.length} struttur{strutture.length === 1 ? 'a' : 'e'}</strong> sincronizzat{strutture.length === 1 ? 'a' : 'e'} dal portale Polizia di Stato.
            {strutture.length > 1 && ' Quando crei una prenotazione, scegli la struttura corretta dal menu — le schedine saranno inviate a quella specifica.'}
          </p>
          <button
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/alloggiati/strutture/sync`, {}, { withCredentials: true });
                toast.success(`Aggiornate ${r.data.count} strutture`);
                fetchAll();
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Errore sync');
              }
            }}
            className="text-[10px] font-black uppercase tracking-wider text-blue-900 hover:text-blue-700 px-2 py-1 border border-blue-300 hover:border-blue-500 transition-colors"
            data-testid="sync-strutture-btn"
          >
            ↻ Aggiorna
          </button>
        </div>
      )}

      {(strutture.length > 0 || properties.length > 0) && <BookingForm strutture={strutture} fallbackProperties={properties} onCreated={onCreated} />}

      <div>
        <p className="text-xs font-black uppercase tracking-wider text-zinc-500 mb-2">
          Prenotazioni · {bookings.length}
        </p>
        <BookingList items={bookings} onDelete={onDelete} onSend={onSend} sendingId={sendingId} />
      </div>

      <div className="bg-zinc-50 border border-zinc-200 p-4 text-[11px] text-zinc-600 leading-relaxed">
        <p className="font-bold text-zinc-700 mb-1">⚙️ Come funziona il flusso completo</p>
        <p>
          • <strong>1. Crea prenotazione</strong> qui sopra → genera link multilingua<br />
          • <strong>2. Invia link</strong> all'ospite via WhatsApp/email<br />
          • <strong>3. Ospite compila</strong> i dati con scansione MRZ del documento (zero upload immagini)<br />
          • <strong>4. Quando completa</strong>, lo status diventa "Pronto invio"<br />
          • <strong>5. Tu invii</strong> con il bottone "Invia alla Polizia ora" — oppure aspetti l'<strong>auto-invio automatico alle 23:30 (orario italiano)</strong> ogni sera<br />
          • <strong>6. Auto-purge GDPR</strong>: i dati personali vengono cancellati immediatamente dopo la conferma di trasmissione
        </p>
      </div>
    </div>
  );
}
